import { User } from "features/user/types";

type UserWithEntitlements = {
  preferences?: {
    entitlements?: Record<string, string | undefined>;
  };
};

/**
 * Pure value parser shared by every entitlement check (main app + Insights).
 *
 * Treats `"true"` (case-insensitive) and any positive numeric string as
 * granted, and treats `"false"`, `"0"`, the empty string, and `undefined`
 * as not-granted. Centralises the rule so we don't drift if the backend
 * later starts emitting new shapes.
 */
export const isEntitlementValueTruthy = (
  value: string | undefined | null
): boolean => {
  if (!value) return false;
  // Accept case variants — server historically emitted lower-case "true" but
  // this normalises against any future "TRUE"/"True" without surprising callers.
  if (value.toLowerCase() === "true") return true;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0;
};

/**
 * Shared entitlement checker used by feature gates and the reverse trial logic.
 *
 * Entitlements live at user.preferences.entitlements as Record<string, string>.
 * Values are typically "true" (boolean features) or numeric strings (limits).
 *
 * Use this helper instead of reaching into user.preferences directly so all
 * checks share the same "is granted" semantics.
 */
export const hasEntitlement = (
  user: UserWithEntitlements | null | undefined,
  key: string
): boolean => isEntitlementValueTruthy(user?.preferences?.entitlements?.[key]);

/**
 * Returns the raw numeric value of an entitlement key, or null if not set.
 * Use this when you need the actual limit (e.g., "accounts" → 5).
 *
 * Note on `"0"`: this returns `0`, not `null`. Callers that want
 * "no positive limit configured" semantics should either compare with
 * `> 0` (as plan-limits.ts does) or use `parsePositiveIntEntitlement`
 * from `utils/entitlements-permission.ts`, which collapses `"0"` to
 * `null`. Kept distinct here because some entitlements (e.g. usage
 * counters) legitimately read `0` and we don't want to mask that.
 *
 * SKE-3484 follow-up: "Unlimited" / "unlimited" / "∞" string values
 * (a Chargebee entitlement convention for headroom plans) collapse to
 * `Number.POSITIVE_INFINITY` here so downstream guards treat them as
 * "always-has-room" rather than `Number(value) → NaN → null` (which
 * would have masked the headroom and routed the user to manage-plan).
 */
export const getEntitlementNumber = (
  user: User | null | undefined,
  key: string
): number | null => {
  const value = user?.preferences?.entitlements?.[key];
  if (!value) return null;
  if (typeof value === "string") {
    const normalised = value.trim().toLowerCase();
    if (normalised === "unlimited" || normalised === "∞") {
      return Number.POSITIVE_INFINITY;
    }
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Returns the raw string value of an entitlement key, or undefined if not set.
 * Use this for enum-valued entitlements (e.g., "post-status-version" → "basic"/"standard"/"advanced").
 *
 * CAVEAT: an explicit `null` value is coerced to `undefined` (via `?? undefined`),
 * so this helper CANNOT distinguish "key absent" from "key present but null".
 * Callers that need that distinction (e.g. to honour an explicit no-access
 * value) must check key presence separately with `hasOwnProperty` /
 * `key in entitlements` BEFORE calling this — see `hasApprovals` in
 * feature-gates.ts and `getPostStatusVersion` in entitlements-permission.ts.
 */
export const getEntitlementValue = (
  user: User | null | undefined,
  key: string
): string | undefined => {
  return user?.preferences?.entitlements?.[key] ?? undefined;
};

/**
 * True if the user has any entitlements at all. Used as a proxy for
 * "the backend has set up entitlements for this account" so we can safely
 * prefer entitlement-first gates over plan-code gates.
 */
export const hasAnyEntitlement = (user: User | null | undefined): boolean => {
  const entitlements = user?.preferences?.entitlements;
  return !!entitlements && Object.keys(entitlements).length > 0;
};
